<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { iSelect, iStepper, iMenuTriggerAlignOptions } from '@inscribe/signal';
import {
  type InsightTransactionCondition,
  type BaseFieldFilter,
  type AmountFilter,
  type TimeframeFilter,
  TransactionConditionOperator,
  OperatorValueType,
  AmountFieldFilterOperator,
  TimeframeMode,
  TransactionBaseFieldFilterOperator,
} from '@/types/decision-engine/configurable-insights.types';
import BaseFieldFilterEditor from '@/V2/decision-engine/configurable-insights/components/filters/BaseFieldFilterEditor.vue';
import AmountFilterEditor from '@/V2/decision-engine/configurable-insights/components/filters/AmountFilterEditor.vue';
import TimeframeFilterEditor from '@/V2/decision-engine/configurable-insights/components/filters/TimeframeFilterEditor.vue';
import FiltersEmptyState from '@/V2/decision-engine/configurable-insights/components/filters/FiltersEmptyState.vue';
import AddFilterButton from '@/V2/decision-engine/configurable-insights/components/filters/AddFilterButton.vue';
import { useConfigurableInsightsStore } from '@/V2/stores/pinia/decision-engine/configurable-insights.store';
import { useConditionEditor } from '@/V2/decision-engine/configurable-insights/composables/useConditionEditor';

interface Props {
  condition: InsightTransactionCondition;
}

interface Emits {
  (e: 'update:condition', value: InsightTransactionCondition): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Initialize store and fetch enrichment values
const store = useConfigurableInsightsStore();

onMounted(() => {
  store.fetchTransactionEnrichmentValues();
});

// Filter types available for transactions
type TransactionFilterType =
  | 'category'
  | 'method'
  | 'merchant'
  | 'payment_processor'
  | 'amount'
  | 'timeframe';

enum FilterEditorType {
  BASE = 'base',
  AMOUNT = 'amount',
  TIMEFRAME = 'timeframe',
}

// Filter configuration
interface FilterConfig {
  key: TransactionFilterType;
  label: string;
  editorType: FilterEditorType;
  placeholder?: string;
  options?: Array<{ text: string; value: string }>;
  defaultValue: BaseFieldFilter | AmountFilter | TimeframeFilter;
}

const FILTER_CONFIGS = computed<FilterConfig[]>(() => [
  {
    key: 'category',
    label: 'Category',
    editorType: FilterEditorType.BASE,
    options: store.getTransactionCategories,
    defaultValue: {
      operator: TransactionBaseFieldFilterOperator.IS_ONE_OF,
      values: [],
    },
  },
  {
    key: 'method',
    label: 'Method',
    editorType: FilterEditorType.BASE,
    options: store.getTransactionMethods,
    defaultValue: {
      operator: TransactionBaseFieldFilterOperator.IS_ONE_OF,
      values: [],
    },
  },
  {
    key: 'merchant',
    label: 'Merchant',
    editorType: FilterEditorType.BASE,
    placeholder: 'e.g., Walmart, Amazon',
    defaultValue: {
      operator: TransactionBaseFieldFilterOperator.IS_ONE_OF,
      values: [],
    },
  },
  {
    key: 'payment_processor',
    label: 'Payment Processor',
    editorType: FilterEditorType.BASE,
    options: store.getPaymentProcessors,
    defaultValue: {
      operator: TransactionBaseFieldFilterOperator.IS_ONE_OF,
      values: [],
    },
  },
  {
    key: 'amount',
    label: 'Amount',
    editorType: FilterEditorType.AMOUNT,
    defaultValue: {
      operator: AmountFieldFilterOperator.GREATER_THAN,
      values: [0],
    },
  },
  {
    key: 'timeframe',
    label: 'Timeframe',
    editorType: FilterEditorType.TIMEFRAME,
    defaultValue: {
      frequency: null,
      time_period_in_days: null,
      mode: TimeframeMode.SLIDING_WINDOW,
    },
  },
]);

// Operator options
const operatorOptions = [
  { text: 'Any', value: TransactionConditionOperator.ANY },
  { text: 'Greater than', value: TransactionConditionOperator.GREATER_THAN },
  { text: 'Less than', value: TransactionConditionOperator.LESS_THAN },
];

// Operator value type options (only shown when operator is not ANY)
const operatorValueTypeOptions = [
  { text: 'transactions match', value: OperatorValueType.ABSOLUTE },
  { text: '% of transactions match', value: OperatorValueType.PERCENTAGE },
];

// Computed flags
const isOperatorAny = computed(
  () => props.condition.operator === TransactionConditionOperator.ANY,
);

const showValueFields = computed(() => !isOperatorAny.value);

// Update handlers
const updateOperator = (value: TransactionConditionOperator) => {
  const updated = { ...props.condition };
  updated.operator = value;

  // If operator is ANY, clear value fields
  if (value === TransactionConditionOperator.ANY) {
    updated.operator_value_type = null;
    updated.operator_value = null;
  } else if (!updated.operator_value_type) {
    // If switching from ANY to something else, set defaults
    updated.operator_value_type = OperatorValueType.ABSOLUTE;
    updated.operator_value = 0;
  }

  emit('update:condition', updated);
};

const updateOperatorValueType = (value: OperatorValueType) => {
  emit('update:condition', {
    ...props.condition,
    operator_value_type: value,
  });
};

const updateOperatorValue = (value: number) => {
  emit('update:condition', {
    ...props.condition,
    operator_value: value,
  });
};

// Available filter types (those not yet added)
const availableFilters = computed(() =>
  FILTER_CONFIGS.value
    .filter((config) => !props.condition.filters[config.key])
    .map((config) => ({
      text: config.label,
      value: config.key,
    })),
);

type TransactionFilterValue = BaseFieldFilter | AmountFilter | TimeframeFilter;

const { activeFilters, addFilter, removeFilter, updateFilter } =
  useConditionEditor<
    InsightTransactionCondition,
    TransactionFilterValue,
    FilterConfig
  >(
    () => props.condition,
    emit,
    () => FILTER_CONFIGS.value,
  );
</script>

<template>
  <div class="transaction-condition-editor d-flex flex-column i-gap-6">
    <div class="header d-flex flex-row align-items-center i-gap-6">
      <div class="condition-text i-text-body-2">Flag if</div>
      <!-- Operator Selection -->
      <i-select
        :model-value="condition.operator"
        :options="operatorOptions"
        placeholder="Select operator"
        data-testid="transaction-operator-select"
        @update:model-value="updateOperator"
      />

      <!-- Operator Value Type & Value (shown when operator is not ANY) -->
      <div
        v-if="showValueFields"
        class="value-fields d-flex flex-row align-items-center i-gap-6"
      >
        <!-- Operator Value -->
        <div class="d-flex flex-column i-gap-3">
          <i-stepper
            class="operator-value-stepper"
            data-testid="operator-value-stepper"
            :model-value="condition.operator_value ?? 0"
            :max-value="
              condition.operator_value_type === OperatorValueType.PERCENTAGE
                ? 100
                : Infinity
            "
            :full-width="true"
            :precision="2"
            :enabled="true"
            size="medium"
            @update:model-value="updateOperatorValue"
          />
        </div>
        <!-- Operator Value Type -->
        <i-select
          class="select-operator-value-type custom-width"
          :model-value="condition.operator_value_type"
          :options="operatorValueTypeOptions"
          placeholder="Select type"
          @update:model-value="updateOperatorValueType"
        />
      </div>
      <div v-else>
        <div class="condition-text i-text-body-2">transactions match</div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section d-flex flex-column i-gap-6">
      <!-- Active Filters -->
      <div
        v-if="activeFilters.length > 0"
        class="active-filters d-flex flex-column i-gap-6 i-p-11 i-radius-5xl"
      >
        <template v-for="filterConfig in activeFilters" :key="filterConfig.key">
          <!-- Base Field Filters -->
          <BaseFieldFilterEditor
            v-if="filterConfig.editorType === FilterEditorType.BASE"
            :filter="filterConfig.value as BaseFieldFilter"
            :label="filterConfig.label"
            :options="filterConfig.options"
            :placeholder="filterConfig.placeholder"
            @update:filter="updateFilter(filterConfig.key, $event)"
            @remove="removeFilter(filterConfig.key)"
          />

          <!-- Amount Filter -->
          <AmountFilterEditor
            v-else-if="filterConfig.editorType === FilterEditorType.AMOUNT"
            :filter="filterConfig.value as AmountFilter"
            :label="filterConfig.label"
            @update:filter="updateFilter(filterConfig.key, $event)"
            @remove="removeFilter(filterConfig.key)"
          />

          <!-- Timeframe Filter -->
          <TimeframeFilterEditor
            v-else-if="filterConfig.editorType === FilterEditorType.TIMEFRAME"
            :filter="filterConfig.value as TimeframeFilter"
            @update:filter="updateFilter(filterConfig.key, $event)"
            @remove="removeFilter(filterConfig.key)"
          />
        </template>

        <!-- Add Filter Button (shown when filters exist) -->
        <AddFilterButton
          :available-filters="availableFilters"
          @add-filter="(val) => addFilter(val as TransactionFilterType)"
        />
      </div>

      <!-- Empty State with Add Filter button inside -->
      <FiltersEmptyState v-else>
        <template #action>
          <AddFilterButton
            :available-filters="availableFilters"
            :trigger-align="iMenuTriggerAlignOptions.CENTER"
            @add-filter="(val) => addFilter(val as TransactionFilterType)"
          />
        </template>
      </FiltersEmptyState>
    </div>
  </div>
</template>

<style scoped lang="scss">
.transaction-condition-editor {
  --operator-stepper-width: 200px;
  --operator-type-width: 250px;

  .condition-text {
    color: var(--text-neutral-bold);
  }

  .operator-value-stepper {
    width: var(--operator-stepper-width);
  }

  .select-operator-value-type {
    width: var(--operator-type-width);
  }

  .field-label {
    color: var(--text-neutral);
  }

  .filters-section {
    .section-label {
      color: var(--text-neutral-bold);
    }
  }
}
</style>
